'use client'

import {useMemo} from 'react'
import {Collapse} from 'antd'
import {ChevronUp} from 'lucide-react'
import type {Filter, RangeFilter, SelectFilter, CheckboxFilter} from '../types'
import {FilterRange} from './filter-range'
import {FilterCheckboxValues} from './filter-checkbox-values'
import {FilterButtonValues} from './filter-button-values'
import * as UI from './filters.styled'

export interface FilterCollapseProps {
  filter: Filter
  onChange: (code: string, value: unknown) => void
  className?: string
}

export function FilterCollapse({filter, onChange, className}: FilterCollapseProps) {
  return (
    <Collapse
      className={className}
      key={filter.code}
      expandIconPosition="end"
      expandIcon={(panelProps) => (
        <UI.ExpandIcon as={ChevronUp} data-active={panelProps.isActive} size={20} />
      )}
      items={[
        {
          key: filter.code,
          label: (
            <UI.FilterCollapseLabel>
              <FilterTitle filter={filter} />
            </UI.FilterCollapseLabel>
          ),
          children: <FilterCollapseBody filter={filter} onChange={onChange} />,
        },
      ]}
    />
  )
}

function FilterTitle({filter}: {filter: Filter}) {
  const selectedCount = useMemo(() => {
    if (filter.type === 'range') return 0
    return filter.values.filter((v) => v.selected).length
  }, [filter])

  return (
    <UI.FilterCollapseTitle>
      <UI.FilterCollapseTitleText>{filter.title}</UI.FilterCollapseTitleText>
      {filter.type === 'range' && (filter.selected_from || filter.selected_to) && (
        <span>
          {filter.selected_from} - {filter.selected_to}
        </span>
      )}
      {filter.type !== 'range' && selectedCount > 0 && (
        <UI.ActiveValuesCounter>{selectedCount}</UI.ActiveValuesCounter>
      )}
    </UI.FilterCollapseTitle>
  )
}

function FilterCollapseBody({
  filter,
  onChange,
}: {
  filter: Filter
  onChange: (code: string, value: unknown) => void
}) {
  if (filter.type === 'range') {
    return (
      <FilterRange
        filter={filter as RangeFilter}
        onChange={(code, value) => onChange(code, value)}
      />
    )
  }

  const isMulti =
    filter.type === 'checkbox' || (filter.type === 'select' && filter.multiple === true)

  return (
    <UI.ValuesWrapper>
      {isMulti ? (
        <FilterCheckboxValues
          filter={filter as CheckboxFilter | (SelectFilter & {multiple: true})}
          onChange={(code, values) => onChange(code, values)}
        />
      ) : (
        <FilterButtonValues
          filter={filter as SelectFilter}
          onChange={(code, value) => onChange(code, value)}
        />
      )}
    </UI.ValuesWrapper>
  )
}
